import { useState, useCallback, useRef, useEffect } from 'react';
import {
  chatStreamLoadingAtom,
  getSessionId,
  pushEvent,
  updateOrPushEvent,
  setDashboardConfigMap,
  useNexaStore,
  activeReportVersionAtom,
  isReportPanelOpenAtom,
  isReportGeneratingAtom,
  getEntrypointParams,
  setEntrypointParams,
  suggestedActionsAtom,
  actionBubblesLoadingAtom,
  showActionButtonsAtom,
} from '../NexaStore';
import {
  handleAgentStatusEventAtom,
  handleAgentResponseRenderedAtom,
  clearAgentStatusAtom,
  handleReportStreamEventAtom,
  handleReportToolCallAtom,
  handleReportToolStateAtom,
  clearReportStreamAtom,
} from '../NexaHandlers';
import config from '@src/api/config';
import instance from '@src/api/axiosInstance';
import { get } from 'lodash';
import { AUTHOR_NEXA_AGENT, supportedEventTypes } from '../utils';
import { useSetAtom, useStore } from 'jotai';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_CHAT_STREAM_ERROR, NEXA_REPORT_GENERATE } from '@src/utils/sensor/track';
import shortId from '@src/utils/shortId';
import { logger } from '@src/utils/misc/logger';
import { useCancelChat } from './useCancelChat';
import { useQueryClient } from '@tanstack/react-query';

const { module = 'EI' } = window.conviva;
const mode = module.toLocaleLowerCase();

interface ErrorObject {
  message?: string;
  details?: string;
  [key: string]: any;
}

export function useChatStream() {
  const setIsLoading = useSetAtom(chatStreamLoadingAtom);
  const setIsReportGenerating = useSetAtom(isReportGeneratingAtom);
  const setSuggestedActions = useSetAtom(suggestedActionsAtom);
  const setActionBubblesLoading = useSetAtom(actionBubblesLoadingAtom);
  const clearReportStream = useSetAtom(clearReportStreamAtom);
  const dispatchAgentStatus = useSetAtom(handleAgentStatusEventAtom);
  const dispatchAgentRendered = useSetAtom(handleAgentResponseRenderedAtom);
  const clearAgentStatus = useSetAtom(clearAgentStatusAtom);
  const dispatchReportStream = useSetAtom(handleReportStreamEventAtom);
  const dispatchReportToolCall = useSetAtom(handleReportToolCallAtom);
  const dispatchReportToolState = useSetAtom(handleReportToolStateAtom);
  const store = useStore();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const sessionId = useNexaStore((state) => state.id);
  // Track accumulated text for partial streaming events
  const eventAccumulator = useRef<Map<string, string>>(new Map());

  const fetchActionBubbles = useCallback(
    async (sessionId: string) => {
      try {
        setActionBubblesLoading(true);
        logger.log('[ActionBubbles] Fetching action suggestions for session:', sessionId);

        const response = await instance.post('/nexa/actions/suggest', {
          session_id: sessionId,
        });

        const data = response.data;
        if (data?.status === 'success' && data.suggested_actions?.length > 0) {
          logger.log('[ActionBubbles] Received actions:', data.suggested_actions);
          setSuggestedActions(data.suggested_actions);
        } else {
          logger.log('[ActionBubbles] No actions returned');
          setSuggestedActions([]);
        }
      } catch (err) {
        logger.error('[ActionBubbles] Failed to fetch action suggestions:', err);
        setSuggestedActions([]);
      } finally {
        setActionBubblesLoading(false);
      }
    },
    [setSuggestedActions, setActionBubblesLoading],
  );

  const fetchStream = useCallback(
    ({
      query,
      diagnostic = undefined,
      analysis_id = undefined,
    }: {
      query: string;
      diagnostic?: { type: string; params?: any };
      analysis_id?: number;
    }) => {
      setIsLoading(true);
      setError(null);

      // Store entrypoint params on first request (if not already stored)
      const currentParams = getEntrypointParams();
      if (!currentParams && (analysis_id || diagnostic)) {
        const newParams: any = {};
        if (analysis_id) {
          newParams.analysis_id = analysis_id;
        }
        if (diagnostic) {
          newParams.diagnostic_type = diagnostic.type;
          newParams.diagnostic_params = diagnostic.params;
        }
        setEntrypointParams(newParams);
      }

      pushEvent({
        id: shortId(),
        author: 'user',
        type: 'text',
        value: query,
        timestamp: new Date().toISOString(),
      });

      // Clear action bubbles state when user sends a new message
      setSuggestedActions([]);
      setActionBubblesLoading(false);

      // Create URL with query parameters for EventSource
      const sessionId = getSessionId();
      const paramsObj: Record<string, string> = {
        mode,
        query,
      };

      if (sessionId) {
        paramsObj.session_id = sessionId;
      }

      if (diagnostic) {
        paramsObj.diagnostic = JSON.stringify(diagnostic);
      }

      if (analysis_id) {
        paramsObj.analysis_id = String(analysis_id);
      }

      // Read report panel state at call time (not from stale closure)
      const activeReportVersion = store.get(activeReportVersionAtom);
      const isReportPanelOpen = store.get(isReportPanelOpenAtom);
      if (isReportPanelOpen && activeReportVersion) {
        paramsObj.active_report_version = String(activeReportVersion);
      }

      const params = new URLSearchParams(paramsObj);
      const url = `${config.baseURL}/nexa/chat/stream?${params.toString()}`;

      const eventSource = new EventSource(url, {
        withCredentials: true, // Ensure cookies are sent
      });

      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const parsedChunk = JSON.parse(event.data);
          logger.log('Parsed SSE chunk:', parsedChunk);

          const timestamp = get(parsedChunk, 'timestamp', new Date().toISOString());
          // Always use event ID from backend (stable ID for partial streaming)
          const eventId = get(parsedChunk, 'id', shortId());

          // Handle tool call events (function_call)
          if (Array.isArray(parsedChunk.function_call)) {
            for (const toolCall of parsedChunk.function_call) {
              if (toolCall.name === 'generate_static_report') {
                apptracker(NEXA_REPORT_GENERATE, { id: getSessionId(), source: 'tool_call' });
                dispatchReportToolCall();
              } else {
                logger.log(`[ChatStream] No handler for tool call: ${toolCall.name}`);
              }
            }
          }

          if (parsedChunk.report_stream) {
            dispatchReportStream({ event: parsedChunk.report_stream, queryClient });
          }

          if (parsedChunk.agent_status) {
            dispatchAgentStatus(parsedChunk.agent_status);
          }

          for (const [type, value] of Object.entries(parsedChunk)) {
            // Handle agent events
            if (supportedEventTypes.includes(type)) {
              // Check if this is a thought event
              const isThought = parsedChunk.thought === true;

              if (Array.isArray(value)) {
                value.forEach((rec) => {
                  pushEvent({
                    id: eventId,
                    author: AUTHOR_NEXA_AGENT,
                    type,
                    value: rec,
                    timestamp,
                    ...(isThought && { thought: true }),
                  });
                });
                if (type === 'text') {
                  dispatchAgentRendered();
                }
              } else if (type === 'text') {
                // Handle text events (including thoughts) with conditional accumulation based on partial flag
                const isPartial = parsedChunk.partial === true;

                if (isPartial) {
                  // Partial event - accumulate incremental text (works for both regular text and thoughts)
                  const currentText = eventAccumulator.current.get(eventId) || '';
                  const newText = currentText + value;
                  eventAccumulator.current.set(eventId, newText);

                  // Update or create event in store with accumulated text
                  updateOrPushEvent({
                    id: eventId,
                    author: AUTHOR_NEXA_AGENT,
                    type,
                    value: newText,
                    timestamp,
                    ...(isThought && { thought: true }),
                  });
                  dispatchAgentRendered();
                } else {
                  // Final event - check if we already have accumulated text
                  // ADK may send full events out of order (after other partials have started)
                  const accumulatedText = eventAccumulator.current.get(eventId);

                  // If we have accumulated text, use it (partials are complete)
                  // Otherwise, use the full text from backend (no partials were received)
                  const finalText = accumulatedText || value;

                  eventAccumulator.current.delete(eventId); // Clear accumulator

                  // Update or create event with final complete text
                  updateOrPushEvent({
                    id: eventId,
                    author: AUTHOR_NEXA_AGENT,
                    type,
                    value: finalText,
                    timestamp,
                    ...(isThought && { thought: true }),
                  });
                  dispatchAgentRendered();
                }
              } else {
                // Other event types use pushEvent as before
                pushEvent({
                  id: eventId,
                  author: AUTHOR_NEXA_AGENT,
                  type,
                  value,
                  timestamp,
                  ...(isThought && { thought: true }),
                });
                if (type === 'text') {
                  dispatchAgentRendered();
                }
              }
            }
            // Handle errors
            else if (type === 'error' && value !== null) {
              const errorObj: ErrorObject =
                typeof value === 'object' ? (value as ErrorObject) : { message: String(value) };

              // Log the error with details for debugging
              logger.error('Server error:', errorObj);

              // Track the error event
              apptracker(NEXA_CHAT_STREAM_ERROR, {
                error: errorObj.message || 'Unknown error',
                details: errorObj.details || 'No details provided',
              });

              // Push error event to be displayed to the user
              pushEvent({
                id: eventId,
                author: AUTHOR_NEXA_AGENT,
                type: 'error',
                value: {
                  message: errorObj.message || 'An error occurred. Please try again.',
                  details: errorObj.details || null,
                },
                timestamp,
              });

              // Also set the error state for potential UI indicators
              setError(errorObj.message || 'Server error occurred');

              // Clear report generating state on error
              setIsReportGenerating(false);
              clearReportStream();
            }
            // meta info from agent
            else if (type === 'state' && value !== null && typeof value === 'object') {
              // Clear accumulator for completed event when state arrives (final event marker)
              if (eventId) {
                eventAccumulator.current.delete(eventId);
              }

              // Dispatch tool state events for known tool results
              for (const [, toolState] of Object.entries(value)) {
                if (typeof toolState === 'object' && toolState !== null) {
                  const toolName = (toolState as any).tool_name;
                  if (toolName === 'generate_static_report') {
                    dispatchReportToolState({ stateValue: toolState, queryClient });
                  }
                }
              }

              setDashboardConfigMap(value);
              dispatchAgentRendered();
            }
          }
        } catch (e) {
          logger.error('Error parsing SSE chunk:', e);
          const errorMessage = e instanceof Error ? e.message : String(e);
          apptracker(NEXA_CHAT_STREAM_ERROR, { error: errorMessage });
          pushEvent({
            id: shortId(),
            author: 'nexa_agent',
            type: 'text',
            value: 'Sorry, I could not process the response. Please refresh and try again.',
          });
          setError(errorMessage);
          clearReportStream();
          clearAgentStatus();
        }
      };

      eventSource.onerror = (event) => {
        // EventSource fires onerror on both real errors AND normal server-side close.
        // readyState is CONNECTING (0) on reconnect attempt, CLOSED (2) only after .close().
        const wasOpen = eventSource.readyState !== EventSource.CLOSED;
        if (wasOpen) {
          logger.log('EventSource stream ended');
        } else {
          logger.error('EventSource error:', event);
          setError('Connection error. Please try again.');
        }

        setIsLoading(false);
        eventSourceRef.current = null;
        clearAgentStatus();
        eventAccumulator.current.clear();
        queryClient.invalidateQueries({ queryKey: ['nexa-token-usage'] });

        // Fetch action bubbles after successful stream completion (if enabled)
        if (wasOpen) {
          const currentSessionId = getSessionId();
          if (currentSessionId && store.get(showActionButtonsAtom)) {
            fetchActionBubbles(currentSessionId);
          }
        }
        eventSource.close();

        // Don't clear report stream state here — let the report stream lifecycle
        // manage its own cleanup (via handleReportToolStateAtom and
        // isPersistedTargetReady effect). Clearing here races with the tool state
        // handler and wipes accumulated streaming HTML before the persisted version loads.
      };

      eventSource.onopen = () => {
        logger.log('EventSource connection opened');
      };
    },
    // All dispatchers from useSetAtom are referentially stable. queryClient and store are
    // also stable. activeReportVersion/isReportPanelOpen are read via store.get() at call
    // time inside the callback, so they are not deps here.
    [],
  );

  const cancelChatMutation = useCancelChat();

  const cancelStream = useCallback(() => {
    // Only cancel if there's an active EventSource connection
    if (eventSourceRef.current) {
      logger.log('[useChatStream] Canceling active stream for session:', sessionId);

      eventSourceRef.current.close();
      eventSourceRef.current = null;

      // Reset loading state
      setIsLoading(false);
      setIsReportGenerating(false);
      clearReportStream();
      clearAgentStatus();

      // Clear event accumulator on cancellation
      eventAccumulator.current.clear();

      // Push a cancellation event only if there was an active stream
      pushEvent({
        id: shortId(),
        author: AUTHOR_NEXA_AGENT,
        type: 'text',
        value: 'Request was canceled.',
        timestamp: new Date().toISOString(),
      });

      // Notify the backend to cancel processing
      if (sessionId) {
        logger.log('Calling cancel API for session:', sessionId);
        cancelChatMutation.mutate({ sessionId });
      }
    } else {
      logger.log('[useChatStream] No active stream to cancel');
    }
  }, [cancelChatMutation, sessionId]);

  useEffect(() => {
    return () => {
      // cancel an active stream on unmount or session change
      cancelStream();
    };
  }, [sessionId]);

  return { error, fetchStream, cancelStream };
}
